/**
 * Product Data Shop.
 */

import { DeclarationOfPerformance } from '../../../models'
import { ElbridgeConfigurator } from '../../../models/Elbridge/ElbridgeConfigurator'
import { LabelUnion, StockAvailabilityEstimate } from '@obeta/schema'
import { MaybeCompleteSearchProduct } from '../../../schema-models'

export enum ProductType {
  custom = 'custom',
  special = 'special',
  normal = 'normal',
  oversize = 'oversize',
}

export enum ProductAttachmentType {
  colored_image = 'colored_image',
  bw_image = 'bw_image',
  milieu_image = 'milieu_image',
  productfamilyimage = 'productfamilyimage',
  measured_drawing = 'measured_drawing',
  exploded_drawing = 'exploded_drawing',
  drawing = 'drawing',
  detailed_image = 'detailed_image',
  logo = 'logo',
  icon = 'icon',
  erp_icon = 'erp_icon',
  sealofapproval = 'sealofapproval',
  placeholder_image = 'placeholder_image',
  energy_label = 'energy_label',
  datasheet_cover = 'datasheet_cover',
  erp_datasheet = 'erp_datasheet',
  msds = 'msds',
  item_datasheet = 'item_datasheet',
  technical_datasheet = 'technical_datasheet',
  wiring_diagram = 'wiring_diagram',
  circuit_diagram = 'circuit_diagram',
  technical_drawing = 'technical_drawing',
  dop = 'dop',
  rohs = 'rohs',
  coc = 'coc',
  doc = 'doc',
  warranty = 'warranty',
  reach = 'reach',
  weee = 'weee',
  approval = 'approval',
  singleitembrochure = 'singleitembrochure',
  brochure = 'brochure',
  luminaire_data = 'luminaire_data',
  lightconediagram = 'lightconediagram',
  lightdistributioncurve = 'lightdistributioncurve',
  cadtopview = 'cadtopview',
  cadfrontview = 'cadfrontview',
  cadsideview = 'cadsideview',
  three_d = 'three_d',
  bim = 'bim',
  installation_manual = 'installation_manual',
  planning = 'planning',
  user_manual = 'user_manual',
  maintenance_manual = 'maintenance_manual',
  maintenance_instruction = 'maintenance_instruction',
  other_manual = 'other_manual',
  sparepartslist = 'sparepartslist',
  training_slides = 'training_slides',
  motor_efficiency = 'motor_efficiency',
  eco_label = 'eco_label',
  uba_list = 'uba_list',
  acs = 'acs',
  explosion_protection = 'explosion_protection',
  certificate = 'certificate',
  certificate_sustainability = 'certificate_sustainability',
  well_label = 'well_label',
  fire_protection = 'fire_protection',
  corrosion_protection = 'corrosion_protection',
  vds_approval = 'vds_approval',
  noiseprotectionverification = 'noiseprotectionverification',
  testreportlithium_battery = 'testreportlithium_battery',
  environmentalproductdeclaration = 'environmentalproductdeclaration',
  environmentalcodeof_practice = 'environmentalcodeof_practice',
  sustainablebuildingquality_seal = 'sustainablebuildingquality_seal',
  contract_form = 'contract_form',
  product_video = 'product_video',
  installation_video = 'installation_video',
  tutorial_video = 'tutorial_video',
  usage_video = 'usage_video',
  qa_video = 'qa_video',
  other = 'other',
}

export interface ProductAttachment {
  type: ProductAttachmentType
  name: string
  url: string
}

export interface Product {
  id: string
  sapId: string
  obetaId: string
  dehaId: string
  title: string
  unit: string
  sapUnit: string
  isoUnit: string
  minimumAmount: number
  supplierArticleId: string
  supplierId: string
  isSendable: boolean
  isCutProduct: boolean
  priceDimension: number
  priceDimensionExponential: number
  bulkPrice: boolean
  bulkPrices?: [
    {
      unit: string
      amount: number
    }
  ]
  type: ProductType
  supplierArticleId2: string
  supplierArticleId3: string
  oxomiId: string
  eans: string[]
  dehaProductGroupId: string
  isPvArticle: boolean
  isDiscontinued: boolean
  isDeleted: boolean
  isForSale: boolean
  isCurrentlyNotAvailable: boolean
  promoCode: string
  replacementArticleId: string
  replacementArticleSapId: string
  articleDescription: string
  catalogInfo?: CatalogInfo
  catalogDescription: string
  isBlackListed: boolean
  packagingUnit: number
  length: number
  width: number
  height: number
  volume: number
  attachments?: ProductAttachment[]
}

export interface CatalogInfo {
  year1: number
  year2: number
  page: number
}

export interface ProductImage {
  large: string
}

export interface Category {
  id: string
  title: string
  productCount: number
}

export interface ProductVariant {
  sapId: string
  title: string
  product: ProductAggregate
}

export interface EtimData {
  dehaId: string
  version: string
  classId: string
  classText: string
  type: string
  featureId: string
  featureText: string
  order: number
  value1: string
  value2: string
  unit: string
}

export interface ProductAggregate extends Product {
  imageData: {
    sapId?: string
    images: ProductImage[]
  }
  images?: {
    width?: number
    url: string
  }[]
  supplierImageData: {
    sapId?: string
    large: string
  }
  stock?: Stock[]
  stockAvailabilityEstimate?: StockAvailabilityEstimate | null
  prices?: ProductPrices
  related?: ProductAggregate[]
  relatedProducts?: ProductAggregate[]
  etimData: EtimData[]
  variants?: ProductVariant[]
  categories?: {
    deha: Category[]
    obeta: Category[]
  }
  isTopseller?: boolean
  labels?: [LabelUnion]
  elbridgeConfigurators: ElbridgeConfigurator[]
  dop: DeclarationOfPerformance[]
  isCustomTitleReplaced?: boolean
  upSelling?: ProductAggregate[]
  crossSelling?: ProductAggregate[]
  replacementProducts: MaybeCompleteSearchProduct['replacementProducts']
}

export interface Image {
  url: string
  width?: number
}

export interface ProductPrices {
  sapId: string
  listPrice: number
  bulkPrice1: number
  catalogPrice: number
  metalNeAddition: number
  netPrice: number
  tecSelect: number
  strikeThroughPrice: number
  currency: string
}

export interface PriceLabelOptions {
  amount?: number
  unit?: string
  withMetal?: boolean
}

export interface Stock {
  sapId: string
  warehouseId: string
  amount: number
  unit: string
}

export type StockType = 'online' | 'store'

export type ProductStockStatus =
  | 'available'
  | 'partially-available'
  | 'currently-not-available'
  | 'not-available'
  | 'not-for-sale'
